import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { KnowledgeBase } from "@/pages/knowledge-bases/domain/entities/knowledge-base";

export type SynthesisSessionSettingsFieldsProps = {
  idPrefix: string;
  model: string;
  onModelChange: (value: string) => void;
  kbIds: string[];
  onKbIdsChange: (ids: string[]) => void;
  llmOptions: Array<{ value: string; display: string }>;
  kbs: KnowledgeBase[];
  enableForm: boolean;
};

function formatKbTriggerLabel(
  kbIds: string[],
  kbs: KnowledgeBase[],
): string {
  if (kbs.length === 0) return "No knowledge bases";
  if (kbIds.length === 0) return "Select knowledge bases…";
  if (kbIds.length === 1) {
    return kbs.find((k) => k.id === kbIds[0])?.name ?? "Unknown";
  }
  return `${kbIds.length} knowledge bases selected`;
}

export const SynthesisSessionSettingsFields = ({
  idPrefix,
  model,
  onModelChange,
  kbIds,
  onKbIdsChange,
  llmOptions,
  kbs,
  enableForm,
}: SynthesisSessionSettingsFieldsProps) => {
  const modelId = `${idPrefix}-model`;
  const kbLabelId = `${idPrefix}-kb-label`;
  const [kbPopoverOpen, setKbPopoverOpen] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Label htmlFor={modelId}>Model</Label>
        <select
          id={modelId}
          className={cn("flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring", !enableForm && "opacity-50 cursor-not-allowed")}
          value={model}
          onChange={(e) => onModelChange(e.target.value)}
          disabled={!enableForm}
        >
          {llmOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.display}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <Label id={kbLabelId}>Knowledge bases</Label>
        <Popover
          modal={false}
          open={kbPopoverOpen}
          onOpenChange={setKbPopoverOpen}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-labelledby={kbLabelId}
              className="h-10 w-full justify-between font-normal"
              disabled={kbs.length === 0 || !enableForm}
            >
              <span className="min-w-0 flex-1 truncate text-left text-sm">
                {formatKbTriggerLabel(kbIds, kbs)}
              </span>
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className={cn(
              "z-[100] w-[var(--radix-popper-anchor-width,100%)] max-w-[min(100vw-2rem,24rem)] p-0",
            )}
          >
            <div role="listbox" className="max-h-60 overflow-y-auto p-1.5">
              {kbs.length === 0 ? (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  No knowledge bases yet.
                </p>
              ) : (
                kbs.map((kb) => {
                  const selected = kbIds.includes(kb.id);
                  return (
                    <button
                      key={kb.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onKbIdsChange(
                          selected
                            ? kbIds.filter((id) => id !== kb.id)
                            : [...kbIds, kb.id],
                        );
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                        "transition-colors outline-none",
                        "hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50",
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded-sm border",
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input",
                        )}
                        aria-hidden
                      >
                        {selected ? (
                          <svg
                            viewBox="0 0 16 16"
                            className="size-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path d="M3.5 8.5 L6.5 11.5 L12.5 4.5" />
                          </svg>
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{kb.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
